import {
  Button,
  Flex,
  Heading,
  IconAddFolderLine,
  IconCollectionLine,
  IconFolderLine,
  IconSubaccountsLine,
  IconSyllabusLine,
  IconWarningSolid,
  InlineList,
  Spinner,
  Text,
  View,
} from "@instructure/ui";
import { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { AcademicHierarchyService } from "../../services/AcademicHierarchyService";
import { ConfigService } from "../../services/ConfigService";
import { CourseGroupService } from "../../services/CourseGroupService";
import { CourseGroupTypeService } from "../../services/CourseGroupTypeService";
import { CourseLevelService } from "../../services/CourseLevelService";
import { ProgramLevelService } from "../../services/ProgramLevelService";
import { ProgramTypeService } from "../../services/ProgramTypeService";
import { SubjectService } from "../../services/SubjectService";
import { FeatureContext, UserDetailContext } from "../../utils/Context";
import { sort } from "../../utils/utils";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import FinalizationSettingsModal from "./FinalizationSettingsModal";
import SubLevel from "./Sublevel";

const ROOT = "root";

const ProgramContext = createContext();
const SubjectContext = createContext();
const CourseLevelsContext = createContext();
const CourseGroupsContext = createContext();
const CourseGroupTypesContext = createContext();

const OrganizationalStructure = () => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const userDetails = useContext(UserDetailContext);
  const features = useContext(FeatureContext);
  const [hierarchy, setHierarchy] = useState(null);
  const [parentSpinner, setParentSpinner] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [courseLevels, setCourseLevels] = useState([]);
  const [courseGroupList, setCourseGroupList] = useState([]);
  const [groupTypes, setGroupTypes] = useState([]);
  const [programLevels, setProgramLevels] = useState([]);

  const [deadline, setDeadline] = useState();
  const [deadlineFetchError, setDeadlineFetchError] = useState(false);

  const [binaryBaselineScale, setBinaryBaselineScale] = useState(null);
  const [finalizationModalOpen, setFinalizationModalOpen] = useState(false);

  const finalizeFlag =
    features.find((f) => f.name === "finalize_results_flag")?.value === "TRUE";

  useEffect(() => {
    async function fetchGlobalDeadline() {
      try {
        const deadline = await ConfigService.getGlobalDaysAfterTermDeadline();
        setDeadline(deadline);
      } catch {
        setDeadlineFetchError(true);
      }
    }
    async function fetchBinaryBaselineScale() {
      try {
        const scale = await ConfigService.getBinaryBaselineScale();
        setBinaryBaselineScale(scale);
      } catch {
        // Scale doesn't exist yet — that's fine
        setBinaryBaselineScale(null);
      }
    }
    if (userDetails.isAC()) {
      fetchGlobalDeadline();
      fetchBinaryBaselineScale();
    }
  }, [showNotification, userDetails]);

  useEffect(() => {
    const promises = [
      AcademicHierarchyService.getLevels(ROOT),
      SubjectService.getSubjects({ allSubjects: true }),
      ProgramTypeService.getProgramTypes(),
      ProgramLevelService.getProgramLevels(),
      CourseLevelService.getCourseLevels(),
      CourseGroupService.getCourseGroups(),
      CourseGroupTypeService.getGroupTypes(),
    ];

    Promise.all(promises)
      .then(
        ([
          levels,
          subs,
          programTypes,
          programLevels,
          courseLevels,
          groups,
          groupTypes,
        ]) => {
          setHierarchy(levels);
          setSubjects(
            subs.map((subject) => ({
              id: subject.id,
              title: subject.title,
              name: subject.name,
              label: subject.name,
            }))
          );
          let types = programTypes.map((programType) => ({
            id: programType.id,
            label: programType.name,
          }));
          types = sort(types, (type) => type.label);
          setProgramTypes(types);
          let progLevels = programLevels.map((level) => ({
            id: level.id,
            label: level.name,
          }));
          progLevels = sort(progLevels, (level) => level.label);
          setProgramLevels(progLevels);
          setCourseLevels(
            courseLevels.map((level) => ({
              id: level.id,
              courseLevel: level.courseLevel,
              label: `${t("programs.courseLevel")} ${level.courseLevel} (${
                level.range.start
              } - ${level.range.end})`,
            }))
          );
          groups = sort(groups, (group) => group.name);
          setCourseGroupList(groups);
          setGroupTypes(
            groupTypes.map((type) => ({
              id: type.id,
              label: type.name,
              mode: "view",
            }))
          );
        }
      )
      .catch(() =>
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("academicHierarchy.headerTitle"),
          }),
          ERROR
        )
      );
  }, [showNotification]);

  const handleAddLevel = async () => {
    setParentSpinner(true);
    try {
      const data = await AcademicHierarchyService.createLevel(
        "",
        t("academicHierarchy.newLevel")
      );
      data.ddl_id = data.id;

      setParentSpinner(false);
      setHierarchy((prevHierarchy) => [...prevHierarchy, data]);
      showNotification(
        t("notifications.general.addSuccess", {
          obj: t("academicHierarchy.hierarchyLevel"),
        })
      );
    } catch {
      setParentSpinner(false);
      showNotification(
        t("notifications.general.addError", {
          obj: t("academicHierarchy.hierarchyLevel"),
        }),
        ERROR
      );
    }
  };

  return (
    <View as="div">
      <Heading as="h2" level="h3" border="bottom" margin="0 0 small 0">
        {t("setup.academicHierarchy")}
      </Heading>
      <Flex gap="small" wrap="wrap">
        <Flex.Item>
          <InlineList size="small" variant="unstyled">
            <InlineList.Item>
              {t("academicHierarchy.legend.iconLegend")}:
            </InlineList.Item>
            <InlineList.Item>
              <IconFolderLine /> {t("academicHierarchy.legend.level")}
            </InlineList.Item>
            <InlineList.Item>
              <IconSubaccountsLine /> {t("academicHierarchy.department")}
            </InlineList.Item>
            <InlineList.Item>
              <IconSyllabusLine /> {t("academicHierarchy.legend.program")}
            </InlineList.Item>
            <InlineList.Item>
              <IconCollectionLine /> {t("academicHierarchy.legend.courseGroup")}
            </InlineList.Item>
          </InlineList>
        </Flex.Item>
        {userDetails.isAC() && (
          <Flex.Item textAlign="end" shouldGrow>
            <Button
              renderIcon={
                parentSpinner ? (
                  <Spinner
                    size="x-small"
                    renderTitle={t("global.pleaseWait")}
                  />
                ) : (
                  IconAddFolderLine
                )
              }
              color="primary"
              onClick={handleAddLevel}
              disabled={parentSpinner}
            >
              {t("academicHierarchy.addHierarchyLevel")}
            </Button>
          </Flex.Item>
        )}
        {finalizeFlag && userDetails.isAC() && (
          <Flex.Item size="100%" textAlign="end" shouldGrow>
            <Button
              color="secondary"
              size="small"
              onClick={() => setFinalizationModalOpen(true)}
            >
              {t("finalizationSettings.title")}
            </Button>
            <FinalizationSettingsModal
              open={finalizationModalOpen}
              onClose={(saved) => {
                setFinalizationModalOpen(false);
                if (saved) {
                  ConfigService.getGlobalDaysAfterTermDeadline()
                    .then(setDeadline)
                    .catch(() => {});
                  ConfigService.getBinaryBaselineScale()
                    .then(setBinaryBaselineScale)
                    .catch(() => {});
                }
              }}
              deadline={deadline}
              existingScale={binaryBaselineScale}
            />
          </Flex.Item>
        )}
        {finalizeFlag && userDetails.isAC() && (
          <Flex.Item size="100%" textAlign="end" shouldGrow>
            <Text>
              <Text weight="bold">
                {t("resultsDeadline.defaultResultsFinalizationDeadline")}:{" "}
              </Text>
              {deadlineFetchError ? (
                <Text color="danger">
                  <IconWarningSolid /> {t("notifications.failedToFetch")}
                </Text>
              ) : deadline === undefined ? (
                <Skeleton width="80px" />
              ) : deadline >= 0 ? (
                <Text>
                  {t("resultsDeadline.numDaysAfterTerm", { num: deadline })}
                </Text>
              ) : (
                <Text color="secondary">
                  {t("resultsDeadline.noneSetDeadline")}
                </Text>
              )}
            </Text>
          </Flex.Item>
        )}
      </Flex>
      {hierarchy ? (
        hierarchy.length > 0 ? (
          sort(hierarchy, (item) => item.name).map((level) => (
            <div key={level.ddl_id} style={{ marginTop: 10 }}>
              <SubjectContext.Provider value={{ subjects, setSubjects }}>
                <ProgramContext.Provider
                  value={{ programTypes, programLevels }}
                >
                  <CourseLevelsContext.Provider value={courseLevels}>
                    <CourseGroupsContext.Provider
                      value={[courseGroupList, setCourseGroupList]}
                    >
                      <CourseGroupTypesContext.Provider
                        value={[groupTypes, setGroupTypes]}
                      >
                        <SubLevel
                          key={level.ddl_id}
                          parent={null}
                          setParent={null}
                          level={level}
                          sub={false}
                          hierarchy={hierarchy}
                        />
                      </CourseGroupTypesContext.Provider>
                    </CourseGroupsContext.Provider>
                  </CourseLevelsContext.Provider>
                </ProgramContext.Provider>
              </SubjectContext.Provider>
            </div>
          ))
        ) : (
          <div>{t("academicHierarchy.nothingToShow")}</div>
        )
      ) : (
        <Skeleton
          count={3}
          style={{ height: 64, borderRadius: 5, marginTop: 10 }}
        />
      )}
    </View>
  );
};

export {
  CourseGroupsContext,
  CourseGroupTypesContext,
  CourseLevelsContext,
  OrganizationalStructure,
  ProgramContext,
  SubjectContext,
};
